import { API, deleteRequest, getRequest, getRequestBlob, postRequest, putRequest } from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateOilLogFormValues,
  OilLog,
  OilLogResponseDto,
  OilLogsList,
  OilLogsListDto,
  UpdateOilLogFormValues,
} from './oil-log.dto';
import { mapCreateOilLogDto, mapOilLog, mapUpdateOilLogDto } from './oil-log.mappers';

export const getOilLogs = async (limit: number, page: number): Promise<OilLogsList> => {
  const searchParams = generateSearchParams({ limit, page });
  const { data } = await getRequest<OilLogsListDto>(API.VESSEL, `/oil-log?${searchParams}`);
  return {
    count: data.count,
    items: data.items.map(mapOilLog),
  };
};

export const getOilLogById = async (id: string): Promise<OilLog> => {
  const { data } = await getRequest<OilLogResponseDto>(API.VESSEL, `/oil-log/${id}`);
  return mapOilLog(data);
};

const toRfc3339 = (date: string): string => {
  const d = new Date(date.length === 10 ? `${date}T00:00:00.000Z` : date);
  return Number.isNaN(d.getTime()) ? date : d.toISOString();
};

export const getOilLogsByDate = async (date: string): Promise<OilLog[]> => {
  const { data } = await getRequest<OilLogResponseDto[] | undefined>(
    API.VESSEL,
    `/oil-log/date`,
    false,
    { date: toRfc3339(date) },
  );
  return (data ?? []).map(mapOilLog);
};

export const createOilLog = async (values: CreateOilLogFormValues): Promise<OilLog> => {
  const { data } = await postRequest<OilLogResponseDto, unknown>(
    API.VESSEL,
    `/oil-log`,
    mapCreateOilLogDto(values),
  );
  return mapOilLog(data);
};

export const updateOilLog = async (values: UpdateOilLogFormValues): Promise<OilLog> => {
  const { data } = await putRequest<OilLogResponseDto, unknown>(
    API.VESSEL,
    `/oil-log/${values.id}`,
    mapUpdateOilLogDto(values),
  );
  return mapOilLog(data);
};

export const deleteOilLog = async (id: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/oil-log/${id}`);
};

export const getOilLogPdf = async (id: string, timezone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/oil-log/${id}/pdf`, { timezone });
};
